/**
 * Audit Logs - Hook for managing audit log state and API calls
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useToast } from '../../../hooks/use-toast';
import { useAuth } from '../../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuditLogs = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [estimated, setEstimated] = useState(false);
  const [loadingExactCount, setLoadingExactCount] = useState(false);
  const [pageSize] = useState(50);
  
  // New logs tracking
  const [showNewLogsBanner, setShowNewLogsBanner] = useState(false);
  const [newLogsCount, setNewLogsCount] = useState(0);
  const [newLogsEstimated, setNewLogsEstimated] = useState(false);
  
  const [filters, setFilters] = useState({
    event_type: 'all',
    entity_type: 'all',
    user_id: '',
    actor_id: '',
    success: 'all',
    date_from: '',
    date_to: '',
    search: ''
  });

  const headers = useMemo(() => ({
    'Content-Type': 'application/json'
  }), []);

  // Generate filter hash for caching
  const filterHash = useMemo(() => {
    return JSON.stringify(filters);
  }, [filters]);

  // Fetch logs
  const fetchLogs = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        page_size: pageSize.toString()
      });
      
      // Only add filter params if they have a meaningful value (not 'all' or empty)
      if (filters.event_type && filters.event_type !== 'all') params.append('event_type', filters.event_type);
      if (filters.entity_type && filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.actor_id) params.append('actor_id', filters.actor_id);
      if (filters.success && filters.success !== 'all') params.append('success', filters.success);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(
        `${API_URL}/api/admin/audit/logs?${params.toString()}`,
        { headers, withCredentials: true }
      );
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
      setEstimated(response.data.estimated || false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to fetch audit logs'
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, headers, toast]);

  // Check for new logs
  const checkNewLogs = useCallback(async () => {
    if (page === 1) {
      setShowNewLogsBanner(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/audit/new-logs-count`,
        { headers, withCredentials: true }
      );
      
      const data = response.data;
      if (data.has_new_logs && data.new_logs_count > 0) {
        setNewLogsCount(data.new_logs_count);
        setNewLogsEstimated(data.estimated);
        setShowNewLogsBanner(true);
      } else {
        setShowNewLogsBanner(false);
      }
    } catch (error) {
      console.error('Failed to check new logs:', error);
    }
  }, [page, headers]);

  // Jump to newest
  const handleJumpToNewest = useCallback(async () => {
    try {
      await axios.post(
        `${API_URL}/api/admin/audit/jump-to-newest`,
        {},
        { headers, withCredentials: true }
      );
      
      setPage(1);
      setShowNewLogsBanner(false);
      await fetchLogs(1);
      
      toast({
        title: 'Success',
        description: 'Showing newest audit logs.'
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to jump to newest'
      });
    }
  }, [headers, fetchLogs, toast]);

  // Dismiss banner
  const handleDismissBanner = useCallback(async () => {
    try {
      await axios.post(
        `${API_URL}/api/admin/audit/dismiss-new-logs-banner`,
        {},
        { headers, withCredentials: true }
      );
      setShowNewLogsBanner(false);
    } catch (error) {
      console.error('Failed to dismiss banner:', error);
      setShowNewLogsBanner(false);
    }
  }, [headers]);

  // Get exact count
  const handleGetExactCount = useCallback(async () => {
    setLoadingExactCount(true);
    try {
      const params = new URLSearchParams({ filter_hash: filterHash });
      // Only add filter params if they have a meaningful value (not 'all' or empty)
      if (filters.event_type && filters.event_type !== 'all') params.append('event_type', filters.event_type);
      if (filters.entity_type && filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.actor_id) params.append('actor_id', filters.actor_id);
      if (filters.success && filters.success !== 'all') params.append('success', filters.success);
      
      const response = await axios.get(
        `${API_URL}/api/admin/audit/count/exact?${params.toString()}`,
        { headers, withCredentials: true }
      );
      
      setTotal(response.data.total);
      setEstimated(false);
      
      toast({
        title: 'Count Updated',
        description: `Exact count: ${response.data.total.toLocaleString()} logs`
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to get exact count'
      });
    } finally {
      setLoadingExactCount(false);
    }
  }, [filterHash, filters, headers, toast]);

  // Export logs
  const handleExport = useCallback(async (format = 'csv') => {
    try {
      const params = new URLSearchParams({ format });
      // Only add filter params if they have a meaningful value (not 'all' or empty)
      if (filters.event_type && filters.event_type !== 'all') params.append('event_type', filters.event_type);
      if (filters.entity_type && filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      
      const response = await axios.get(
        `${API_URL}/api/admin/audit/export?${params.toString()}`,
        { headers, withCredentials: true, responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      
      toast({
        title: 'Export Complete',
        description: `Audit logs exported as ${format.toUpperCase()}`
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Export Failed',
        description: 'Failed to export audit logs'
      });
    }
  }, [filters, headers, toast]);

  useEffect(() => {
    fetchLogs();
  }, [page, fetchLogs]);

  useEffect(() => {
    const interval = setInterval(checkNewLogs, 30000);
    return () => clearInterval(interval);
  }, [checkNewLogs]);

  return {
    logs,
    loading,
    page,
    setPage,
    total,
    estimated,
    pageSize,
    loadingExactCount,
    showNewLogsBanner,
    newLogsCount,
    newLogsEstimated,
    filters,
    setFilters,
    fetchLogs,
    handleJumpToNewest,
    handleDismissBanner,
    handleGetExactCount,
    handleExport
  };
};

export default useAuditLogs;
